/**
 * The customer form's "Customer profile" card — its values, progress and select options.
 * Drawn by `@rista/ui/components/CustomerProfileSection`. Pure: no store, no platform API.
 */
import { splitSavedDate } from "../helpers/dateTimeHelper";
import type { DateParts } from "../helpers/dateTimeHelper";
import { isValidEmail } from "../utils/validator";
import { isProfileConfigured } from "./program";
import type { ProfileFieldKey, ProgramDetailsEnvelope } from "./types";

export type { DateParts, ProfileFieldKey };

export type ConsentStatus = "opted_in" | "opted_out" | string;

export interface CustomerProfileValues {
  firstName?: string;
  lastName?: string;
  email?: string;
  gender?: string;
  /** The saved "y-m-d" strings; the card splits them into day/month/year itself. */
  birthDate?: string | null;
  anniversaryDate?: string | null;
  /** Read-only — see `resolveProfileCrmConsents`. */
  crmConsents?: { whatsappMarketing?: ConsentStatus; smsMarketing?: ConsentStatus } | null;
}

export const DEFAULT_NEW_CUSTOMER_CRM_CONSENTS = {
  whatsappMarketing: "opted_in",
  smsMarketing: "opted_in",
};

/** The customer's saved consents, or the opted-in default when there are none. */
export const resolveProfileCrmConsents = (saved?: CustomerProfileValues["crmConsents"]) =>
  saved ?? DEFAULT_NEW_CUSTOMER_CRM_CONSENTS;

export interface ProfilePayloadContext {
  programDetails?: ProgramDetailsEnvelope | null;
  /** The BILL's channel — `selectLoyaltyChannelDefId`. */
  channelDefId?: string;
}

/**
 * The profile half of a customer save, in ONE place for both apps: the saved record, then the
 * form's own fields, then what the CRM needs beside them. `crmConsents` goes only where the
 * merchant's profile config is set up (never as an empty key), and a record with none gets the
 * opted-in default — the single line to change if existing customers should not.
 */
export function mergeCustomerProfile<S extends { crmConsents?: CustomerProfileValues["crmConsents"] }>(
  saved: S | null | undefined,
  info: object,
  { programDetails, channelDefId }: ProfilePayloadContext,
) {
  const { crmConsents, ...rest } = saved ?? ({} as S);
  return {
    ...rest,
    ...info,
    ...(isProfileConfigured(programDetails)
      ? { crmConsents: resolveProfileCrmConsents(crmConsents) }
      : {}),
    ...(channelDefId ? { channelDefId } : {}),
  };
}

/** Everything the card can edit. `crmConsents` is display-only. */
export type ProfileEditableField = Exclude<keyof CustomerProfileValues, "crmConsents">;

/** Every field, dates included, reports a string — a date as "y-m-d", possibly half-picked. */
export type ProfileChangeHandler = (field: ProfileEditableField, value: string) => void;

const isDateFilled = (value?: string | null) => {
  const parts = splitSavedDate(value);
  return !!(parts.d && parts.m && parts.y);
};

const FIELD_FILLED: Record<ProfileFieldKey, (v: CustomerProfileValues) => boolean> = {
  dateOfBirth: (v) => isDateFilled(v.birthDate),
  anniversary: (v) => isDateFilled(v.anniversaryDate),
  email: (v) => !!v.email?.trim(),
  gender: (v) => !!v.gender,
};

/** Name fields are always shown, so they always count; consents never do. */
export function getProfileProgress(visibleFields: ProfileFieldKey[], values: CustomerProfileValues) {
  const filled =
    Number(!!values.firstName?.trim()) +
    visibleFields.filter((field) => FIELD_FILLED[field](values)).length;

  return { filled, total: 1 + visibleFields.length };
}

export interface SelectOption {
  label: string;
  value: string;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export const DAY_OPTIONS: SelectOption[] = Array.from({ length: 31 }, (_, i) => ({
  label: String(i + 1),
  value: String(i + 1),
}));

export const MONTH_OPTIONS: SelectOption[] = MONTHS.map((label, i) => ({ label, value: String(i + 1) }));

// 1900–2100, matching the legacy pickers.
export const YEAR_OPTIONS: SelectOption[] = Array.from({ length: 201 }, (_, i) => ({
  label: String(1900 + i),
  value: String(1900 + i),
}));

export const GENDER_OPTIONS = ["Male", "Female", "Other"] as const;

/**
 * Rules that need only the field's own value: a message when it is wrong, else undefined.
 * The profile section runs them once a field is left, and a host can gate its CTA on the same
 * call. Email is a shape check only; the backend decides the rest.
 */
export const PROFILE_VALIDATORS: Partial<
  Record<ProfileEditableField, (value: string) => string | undefined>
> = {
  firstName: (value) => (value.trim() ? undefined : "Enter first name"),
  email: (value) => {
    const trimmed = value.trim();
    return !trimmed || isValidEmail(trimmed) ? undefined : "Enter a valid email";
  },
};
